<template>
  <ModalDialog header-text="Send Email" :open="open" @close-modal="closeModal">
    <template v-slot:content>
      <form class="email-form" @submit.prevent="onSubmitAsync">
        <p>
          Separate email addresses with semi-colons [no spaces allowed]<br />
          Example: RabbiSmith@ou.org;RabbiCohen@ou.org
        </p>
        <div class="inline-form-control">
          <label for="email-recipient">To:</label>
          <input
            type="text"
            id="email-recipient"
            name="email-recipient"
            v-model="emailRecipient"
          />
        </div>
        <div class="inline-form-control">
          <label for="emailCc">Cc:</label>
          <input type="text" id="emailCc" name="emailCc" v-model="emailCc" />
        </div>
        <div class="inline-form-control">
          <label for="emailBcc">Bcc:</label>
          <input type="text" id="emailBcc" name="emailBcc" v-model="emailBcc" />
        </div>
        <div class="inline-form-control">
          <label for="subject">Subject:</label>
          <input type="text" id="subject" name="subject" v-model="subject" />
        </div>
        <div class="form-control">
          <textarea
            id="message"
            class="message-textarea"
            name="message"
            v-model="message"
            :rows="10"
            placeholder="Enter your message here..."
          ></textarea>
        </div>
        <div class="form-control">
          <label for="upload">Select Document(s):</label>
          <Grid
            class="attachments-grid"
            :data-items="gridAttachments"
            :columns="columns"
            selected-field="selected"
            @selectionchange="changeSelection"
            @rowclick="changeSelection"
            data-cy="attachments-grid"
          >
            <template v-slot:SelectHeaderCell>
              <th>
                <input
                  type="checkbox"
                  :checked="allAttachmentsSelected"
                  ref="selectAllCheckbox"
                  @change="onAttachmentHeaderSelectionChange"
                />
              </th>
            </template>
            <template v-slot:NameCell="{ props: { dataItem, field } }">
              <td class="k-table-td">
                <a
                  class="attachment-link"
                  @click="downloadAttachments(dataItem)"
                >
                  {{ dataItem[field] }}
                </a>
              </td>
            </template>
          </Grid>
        </div>
        <Button
          type="submit"
          class="submit-button"
          theme-color="primary"
          :disabled="message.length == 0 || formSubmitting"
          data-cy="submit-button"
          >Send</Button
        >
      </form>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { useMainStore } from "@/stores/useMainStore";
import { EnumWorkflowType } from "@/Domain/EnumWorkflowType";
import { computed, onBeforeMount, ref,  } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import type { WfSubmissionDisplay } from "@/Domain/WfSubmissionDisplay";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { PersonJobTb } from "@/Domain/PersonJobTb";
import type { WfIngredientSubmissionDisplay } from "@/Domain/WfIngredientSubmissionDisplay";
import type {
  GridColumnProps,
  GridRowClickEvent,
  GridSelectionChangeEvent,
} from "@progress/kendo-vue-grid";
import type { SharePointAttachment } from "@/Domain/SharePointAttachment";
import { Grid } from "@progress/kendo-vue-grid";
import type { WfProductSubmissionDisplay } from "@/Domain/WfProductSubmissionDisplay";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  open: boolean;
  workflowType: EnumWorkflowType;
  workflowId: number;
  jobId: string;
  wfSubmission: WfSubmissionDisplay;
  companyId: number;
  companyName: string;
  attachments: SharePointAttachment[];
  checkedOut?: boolean;
}>();

interface SharePointAttachmentWithSelection extends SharePointAttachment {
  selected: boolean;
}

const emit = defineEmits(["closeModal"]);

const subject = ref<string>("");
const emailRecipient = ref<string>("");
const message = ref<string>("");
const emailCc = ref<string>("");
const emailBcc = ref<string>("");
const formSubmitting = ref<boolean>(false);
const personJob = ref<PersonJobTb | null>(null);

const columns = ref<GridColumnProps[]>([
  {
    field: "selected",
    width: "50px",
    sortable: false,
    filterable: false,
    headerCell: "SelectHeaderCell",
    headerClassName: "hide-menu",
  },
  {
    title: "Document Name",
    field: "Name",
    cell: "NameCell",
  },
]);

const gridAttachments = computed(() => {
  return props.attachments.filter((a, i, arr) => {
    return arr.findIndex((b) => b.Url == a.Url) == i;
  }) as SharePointAttachmentWithSelection[];
});

const allAttachmentsSelected = computed(() => {
  return gridAttachments.value.every((attachment) => attachment.selected);
});

const getCurrentPersonJobAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetCurrentPersonJob", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
    }),
    (response) => {
      personJob.value = response;
    },
    "WorkflowEmailModal.vue",
    "getCurrentPersonJobAsync"
  );
};

const setDefaultValues = function () {
  if (!personJob.value) return;
  const fromEmailAddress = personJob.value!.personTb.Contacts[0].Businessemail;
  const fromEmailName =
    personJob.value.personTb.FirstNameLastNameTitleIfRabbiOnly.trim();
  const fromEmailPhone = personJob.value!.personTb.Contacts[0].Businessphone;

  if (props.workflowType == EnumWorkflowType.Ingredient) {
    // External email for customers, similar to products below
    message.value =
      `\n${props.companyName}/${props.companyId}\nJob #: ${props.jobId}\n\n
      If you have any questions, please do not hesitate to contact me. Thank you for your business and for partnering with the OU.\n\n
      ${fromEmailName}
      Orthodox Union
      Phone: ${fromEmailPhone}
      Email: ${fromEmailAddress}`.replace(/(\n\s+)|(\n\n\s+)/g, (match) => {
        return match.startsWith("\n\n") ? "\n\n" : "\n";
      });

    emailRecipient.value = (
      props.wfSubmission as WfIngredientSubmissionDisplay
    ).Submission.UserEmail.split(";")[0];

    subject.value = `Notification re: Job #: ${
      props.jobId
    } / SubmissionType: ${props.wfSubmission.JobType.replace(
      "Ingredient",
      "Ingredient "
    )}`;
    emailCc.value = fromEmailAddress;
  } else if (props.workflowType == EnumWorkflowType.Product) {
    message.value =
      `\n${props.companyName} (${props.companyId})\nJob #: ${props.jobId}\n\n
      If you have any questions, please do not hesitate to contact me. Thank you for your business and for partnering with the OU.\n\n
      ${fromEmailName}
      Orthodox Union
      Phone: ${fromEmailPhone}
      Email: ${fromEmailAddress}`.replace(/(\n\s+)|(\n\n\s+)/g, (match) => {
        return match.startsWith("\n\n") ? "\n\n" : "\n";
      });

    emailRecipient.value = (
      props.wfSubmission as WfProductSubmissionDisplay
    ).UserEmail?.split(";")[0];

    subject.value = `Notification re: Job #: ${
      props.jobId
    } / SubmissionType: ${props.wfSubmission.JobType.replace(
      "Product",
      "Product "
    )}`;
    emailCc.value = fromEmailAddress;
  }
};

const onSubmitAsync = async function () {
  if (formSubmitting.value) return;

  formSubmitting.value = true;

  const selectedAttachmentIds = gridAttachments.value
    .filter((a) => a.selected)
    .map((a) => a.Url);

  const sendEmailPromise = api.post("SendWorkflowEmail", {
    UserLogin: main.userLogin,
    CompanyLogin: main.companyLogin,
    WorkflowId: props.workflowId,
    JobId: props.jobId,
    WorkflowType: props.workflowType,
    Message: message.value,
    Subject: subject.value,
    SendTo: emailRecipient.value,
    Cc: emailCc.value,
    Bcc: emailBcc.value,
    Attachments: selectedAttachmentIds,
    CheckedOut: props.checkedOut ?? false,
  });

  await handleResponsePromiseAsync(
    sendEmailPromise,
    () => {
      notificationStore.notify({
        message: "Email sent successfully",
        type: NotificationTypeEnum.success,
        duration: 5000,
      });
      closeModal();
    },
    "WorkflowEmailModal.vue",
    "onSubmit",
    (error) => {
      notificationStore.notify({
        message: `Error sending email. Please try again.`,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );

  formSubmitting.value = false;
};

const changeSelection = function (
  event: GridSelectionChangeEvent | GridRowClickEvent
) {
  const className = event.event?.target?.className;
  if (className.includes && className.includes("attachment-link")) return;

  const attachment = event.dataItem as SharePointAttachmentWithSelection;
  if (!attachment) return;
  attachment.selected = !attachment.selected;
};

const onAttachmentHeaderSelectionChange = function (event: Event) {
  const checked = (event.target as HTMLInputElement).checked;
  gridAttachments.value.forEach((a) => {
    a.selected = checked;
  });
};

const downloadAttachments = function (attachment: SharePointAttachment) {
  return api.getDownload("GetWorkflowAttachmentFile", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    jobId: props.jobId.toString(),
    workflowId: props.workflowId,
    attachmentId: attachment.WorkFlowAttachmentId,
    attachmentUrl: attachment.Url,
    cartId: attachment.CartId,
    workflowType:
      props.workflowType == EnumWorkflowType.Product ? "Product" : "Ingredient",
  });
};

const closeModal = function () {
  emit("closeModal");
};

onBeforeMount(async () => {
  await getCurrentPersonJobAsync();
  setDefaultValues();
});
</script>

<style lang="scss">
.email-form {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  gap: 16px;

  @include media-breakpoint-up(sm) {
    width: 500px;
  }

  @include media-breakpoint-up(lg) {
    width: 800px;
  }

  .message-textarea {
    resize: vertical;
  }

  .attachment-link {
    color: #337ab7;
    cursor: pointer;

    &:hover {
      color: #0a58ca;
    }
  }

  .submit-button {
    width: 200px;
    margin: 0 auto;
  }

  .inline-form-control {
    display: flex;
    margin-bottom: 1rem;
    align-items: center;

    label {
      height: 36px;
      line-height: 24px;
      font-size: 1rem;
      background: #f7f7f7;
      padding: 5px 10px;
      border-radius: 4px 0 0 4px;
      border: 1px solid #ddd;
      padding-right: 24px;
      flex-basis: 25%;
    }

    input {
      height: 36px;
      padding: 0.5rem;
      border: 1px solid #ccc;
      font-size: 1rem;
      border-radius: 0 4px 4px 0;
      width: 100%;
      flex-basis: 75%;
    }
  }

  .k-svg-icon {
    width: 16px;
    height: 16px;
  }

  .holiday-message {
    display: block;
    text-align: center;
    padding: 5px;
    margin: 10px 0;
    background: #fff1d9;
    color: #a99c85;
    border: 1px solid #e8d9c0;
  }
}
</style>
